import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, ExternalLink, Clock, Check, Award, ArrowUpRight } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Kicker, WfButton, ImageWithCredit } from "@/components/site/primitives";
import {
  IMPACT_HERO,
  IMPACT_STATS,
  IMPACT_INDEPENDENCE,
  IMPACT_MODEL,
  IMPACT_COMMUNITY,
  IMPACT_REPORTS_INTRO,
  ANNUAL_REPORTS,
} from "@/content/impact";

/* ---------------- Headline numbers ---------------- */
const StatsSection = () => (
  <section id="by-the-numbers" aria-labelledby="impact-stats-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <h2 id="impact-stats-heading" className="font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          By the Numbers
        </h2>
      </Reveal>

      <dl className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {IMPACT_STATS.map((s, i) => (
          <Reveal key={s.id} delay={0.07 * i} className="h-full">
            <div
              data-testid={`impact-stat-${s.id}`}
              className="flex h-full flex-col rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-7 shadow-[var(--wf-shadow-sm)]"
            >
              <dt className="order-2 mt-2 text-sm font-semibold uppercase tracking-[0.12em] text-[var(--wf-green-text)]">{s.label}</dt>
              <dd className="order-1 font-serif text-5xl font-semibold leading-none text-[var(--wf-maroon)] sm:text-6xl">{s.value}</dd>
              <dd className="order-3 mt-4 border-t border-[var(--wf-border)] pt-4 text-sm leading-6 text-[var(--wf-soil)]">{s.detail}</dd>
            </div>
          </Reveal>
        ))}
      </dl>
    </div>
  </section>
);

/* ---------------- Independence ---------------- */
const IndependenceSection = () => (
  <section id="independence" aria-labelledby="impact-independence-heading" className="wf-section bg-white">
    <div className="wf-container">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <ImageWithCredit
            src={IMPACT_INDEPENDENCE.image}
            alt={IMPACT_INDEPENDENCE.imageAlt}
            className="aspect-[4/5] w-full rounded-sm shadow-[var(--wf-shadow)]"
            imgClassName="object-cover"
          />
        </Reveal>
        <Reveal delay={0.1}>
          <Kicker>{IMPACT_INDEPENDENCE.eyebrow}</Kicker>
          <h2 id="impact-independence-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
            {IMPACT_INDEPENDENCE.title}
          </h2>
          <p className="mt-5 max-w-[58ch] text-base leading-7 text-[var(--wf-soil)]">{IMPACT_INDEPENDENCE.body}</p>
          <dl className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {IMPACT_INDEPENDENCE.figures.map((f) => (
              <div key={f.id} data-testid={`impact-independence-${f.id}`} className="rounded-sm bg-[var(--wf-cream)] p-5">
                <dd className="font-serif text-3xl font-semibold text-[var(--wf-maroon)]">{f.value}</dd>
                <dt className="mt-1 text-sm leading-6 text-[var(--wf-soil)]">{f.label}</dt>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- National model ---------------- */
const ModelSection = () => (
  <section id="national-model" aria-labelledby="impact-model-heading" className="relative overflow-hidden bg-[var(--wf-farm-green)] py-20 sm:py-24 lg:py-28">
    <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
    <div className="wf-container relative">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.25fr_1fr] lg:gap-16">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">
            <span aria-hidden="true" className="h-px w-6 bg-[rgba(255,253,248,0.5)]" />
            {IMPACT_MODEL.eyebrow}
          </span>
          <h2 id="impact-model-heading" className="mt-5 flex items-start gap-3 font-serif text-3xl font-semibold leading-[1.15] text-white sm:text-4xl">
            <Award className="mt-1 h-8 w-8 shrink-0 text-[var(--wf-wheat)]" aria-hidden="true" />
            {IMPACT_MODEL.title}
          </h2>
          <p className="mt-6 max-w-[60ch] text-base leading-7 text-[rgba(255,253,248,0.9)]">{IMPACT_MODEL.body}</p>
          <ul className="mt-6 space-y-3">
            {IMPACT_MODEL.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-[rgba(255,253,248,0.92)]">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
                  <Check className="h-3 w-3" aria-hidden="true" />
                </span>
                <span className="text-sm leading-6 sm:text-base">{p}</span>
              </li>
            ))}
          </ul>
          <WfButton as={Link} to="/programs/mentorship" variant="light" withArrow className="mt-8 px-7" data-testid="impact-model-cta">
            Explore the mentorship path
          </WfButton>
        </Reveal>
        <Reveal delay={0.1}>
          <ImageWithCredit
            src={IMPACT_MODEL.image}
            alt={IMPACT_MODEL.imageAlt}
            className="aspect-square w-full rounded-sm shadow-[var(--wf-shadow)] ring-1 ring-white/20"
            imgClassName="object-cover"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Community ---------------- */
const CommunitySection = () => (
  <section id="community" aria-labelledby="impact-community-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <Kicker>{IMPACT_COMMUNITY.eyebrow}</Kicker>
          <h2 id="impact-community-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            {IMPACT_COMMUNITY.title}
          </h2>
          <p className="mt-4 max-w-[56ch] text-base leading-7 text-[var(--wf-soil)]">{IMPACT_COMMUNITY.body}</p>
          <ul className="mt-8 divide-y divide-[var(--wf-border)] border-y border-[var(--wf-border)]">
            {IMPACT_COMMUNITY.facts.map((f) => (
              <li key={f.id} data-testid={`impact-community-${f.id}`} className="flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:gap-4">
                <span className="font-serif text-2xl font-semibold text-[var(--wf-maroon)] sm:w-44 sm:shrink-0">{f.value}</span>
                <span className="text-base leading-7 text-[var(--wf-soil)]">{f.label}</span>
              </li>
            ))}
          </ul>
          <WfButton as={Link} to="/meet-the-farmers" withArrow className="mt-8 px-7" data-testid="impact-community-cta">
            Meet the farmers
          </WfButton>
        </Reveal>
        <Reveal delay={0.1}>
          <ImageWithCredit
            src={IMPACT_COMMUNITY.image}
            alt={IMPACT_COMMUNITY.imageAlt}
            className="aspect-[4/5] w-full rounded-sm shadow-[var(--wf-shadow)]"
            imgClassName="object-cover"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Annual reports ---------------- */
const ReportCard = ({ report, index }) => {
  const available = Boolean(report.href);
  const Icon = report.type === "web" ? ExternalLink : FileText;
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <span
          aria-hidden="true"
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${
            available ? "bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]" : "bg-[rgba(59,47,42,0.08)] text-[var(--wf-soil)]"
          }`}
        >
          {available ? <Icon className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
        </span>
        {!available && (
          <span className="whitespace-nowrap rounded-full border border-[var(--wf-border)] bg-[var(--wf-cream)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--wf-soil)]">
            Coming soon
          </span>
        )}
      </div>
      <p className={`mt-6 font-serif text-4xl font-semibold leading-none ${available ? "text-[var(--wf-maroon)]" : "text-[var(--wf-soil)]/60"}`}>
        {report.year}
      </p>
      <p className="mt-2 text-sm font-semibold text-[var(--wf-ink)]">Annual Report</p>
      {report.subtitle && <p className="mt-1 text-xs leading-5 text-muted-foreground">{report.subtitle}</p>}
      <p className={`mt-4 text-xs font-semibold uppercase tracking-[0.12em] ${available ? "text-[var(--wf-green-text)]" : "text-muted-foreground"}`}>
        {available ? (report.type === "web" ? "View report" : "Download PDF") : "PDF will be posted here"}
      </p>
    </>
  );

  const base = "flex h-full flex-col rounded-sm border p-6 transition-[box-shadow,transform] duration-200";
  return available ? (
    <a
      href={report.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${report.title}${report.subtitle ? ` \u2014 ${report.subtitle}` : ""} (opens in a new tab)`}
      data-testid={`annual-report-${report.year}`}
      className={`${base} border-[var(--wf-border)] bg-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)] hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background`}
    >
      {inner}
    </a>
  ) : (
    <div
      aria-disabled="true"
      aria-label={`${report.title} \u2014 coming soon`}
      data-testid={`annual-report-${report.year}`}
      className={`${base} border-dashed border-[var(--wf-border)] bg-[var(--wf-cream)]/60`}
    >
      {inner}
    </div>
  );
};

const ReportsSection = () => (
  <section id="annual-reports" aria-labelledby="impact-reports-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="mx-auto max-w-2xl text-center">
        <Kicker className="justify-center">{IMPACT_REPORTS_INTRO.eyebrow}</Kicker>
        <h2 id="impact-reports-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          {IMPACT_REPORTS_INTRO.title}
        </h2>
        <p className="mt-4 text-base leading-7 text-muted-foreground">{IMPACT_REPORTS_INTRO.body}</p>
      </Reveal>

      <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5" data-testid="annual-reports-grid">
        {ANNUAL_REPORTS.map((r, i) => (
          <Reveal key={r.year} delay={0.05 * i} as="li" className="h-full">
            <ReportCard report={r} index={i} />
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------------- A snapshot of success ---------------- */
const SNAPSHOT_REPORTS = [
  {
    id: "2026",
    title: "2026 Successes in Farmer Development",
    href: "https://www.worldfarmers.org/wp-content/uploads/2026/10/2026-Successess-in-Farmer-Development.pdf",
  },
  {
    id: "2022",
    title: "2022 Successes (BFRDP)",
    href: "https://www.worldfarmers.org/wp-content/uploads/2023/07/2022-Successes_BFRDP-.pdf",
  },
];

const SnapshotSection = () => (
  <section id="snapshot" aria-labelledby="impact-snapshot-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <Reveal className="mx-auto max-w-2xl text-center">
        <h2 id="impact-snapshot-heading" className="font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          A Snapshot of Success
        </h2>
        <p className="mt-4 text-base leading-7 text-muted-foreground">
          Mini updates and reports of our successes in farmer development — shared with funders and our wider community.
        </p>
      </Reveal>
      <ul className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-5 sm:grid-cols-2" data-testid="impact-snapshot-list">
        {SNAPSHOT_REPORTS.map((r) => (
          <li key={r.href}>
            <a
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`impact-snapshot-${r.id}`}
              className="group flex h-full items-start gap-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-5 shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-200 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              <span className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]" aria-hidden="true">
                <FileText className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-serif text-lg font-semibold leading-snug text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">{r.title}</span>
                <span className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[var(--wf-maroon)]">
                  View PDF <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------------- Closing CTA ---------------- */
const OutroSection = () => (
  <section aria-labelledby="impact-outro-heading" className="bg-[var(--wf-cream)] py-16 sm:py-20 lg:py-24">
    <div className="wf-container">
      <div className="relative overflow-hidden rounded-sm bg-[var(--wf-maroon)] px-6 py-14 text-center sm:px-10 sm:py-16">
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--wf-clay)]/30 blur-2xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-[var(--wf-wheat)]/20 blur-2xl" />
        <div className="relative mx-auto max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">Grow the impact</span>
          <h2 id="impact-outro-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">Every gift puts more land into farmers’ hands</h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.85)]">Your support funds training, infrastructure, and market access for the next generation of independent farmers.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <WfButton as={Link} to="/support" variant="light" withArrow className="px-7" data-testid="impact-outro-support-button">Support a farmer</WfButton>
            <WfButton as={Link} to="/history" className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="impact-outro-history-button">Our history</WfButton>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Page ---------------- */
export default function ImpactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...IMPACT_HERO} testid="impact-hero" />
        <StatsSection />
        <ModelSection />
        <SnapshotSection />
        <ReportsSection />
        <OutroSection />
      </main>
      <SiteFooter />
    </>
  );
}
